import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/tab-panel.default.js';
import * as wireframeSkin from './skins/tab-panel.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

/** @type {readonly ['default', 'wireframe']} */
export const TAB_PANEL_SKINS = Object.freeze(['default', 'wireframe']);

class RunTabPanel extends RunElement {
  // ── Private state ──────────────────────────────────────────────────────────
  #name       = '';
  #active     = false;
  #labelledBy = '';
  #skin       = null;
  #rendering  = false;

  // ── Static ─────────────────────────────────────────────────────────────────
  static get observedAttributes() {
    return ['name', 'active', 'aria-labelledby', 'skin'];
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'tabpanel');
    this.#syncHostAriaAttributes();
    this.requestUpdate();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'name':
        this.#name = newValue !== null ? String(newValue) : '';
        break;
      case 'active':
        this.#active = newValue !== null;
        this.#syncHostAriaAttributes();
        this.requestUpdate();
        break;
      case 'aria-labelledby':
        this.#labelledBy = newValue !== null ? String(newValue) : '';
        break;
      case 'skin':
        this.#skin = newValue;
        this.requestUpdate();
        break;
    }
  }

  // ── Public property accessors ──────────────────────────────────────────────

  get name() { return this.#name; }
  set name(value) {
    const coerced = String(value ?? '');
    if (this.#name === coerced) return;
    this.#name = coerced;
    this.setAttribute('name', coerced);
  }

  get active() { return this.#active; }
  set active(value) {
    const coerced = Boolean(value);
    if (this.#active === coerced) return;
    this.#active = coerced;
    this.toggleAttribute('active', coerced);
    this.#syncHostAriaAttributes();
    this.requestUpdate();
  }

  get labelledBy() { return this.#labelledBy; }
  get ariaLabelledby() { return this.getAttribute('aria-labelledby'); }
  set labelledBy(value) {
    const coerced = String(value ?? '');
    if (this.#labelledBy === coerced) return;
    this.#labelledBy = coerced;
    if (coerced) {
      this.setAttribute('aria-labelledby', coerced);
    } else {
      this.removeAttribute('aria-labelledby');
    }
  }

  get skin() { return this.#skin; }
  set skin(value) {
    const coerced = value !== null && value !== undefined ? String(value) : null;
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced !== null) {
      this.setAttribute('skin', coerced);
    } else {
      this.removeAttribute('skin');
    }
    this.requestUpdate();
  }

  // ── Skin wiring ────────────────────────────────────────────────────────────

  // Return undefined so RunElement._render() does not write to the shadow root.
  // The skin writes DOM directly in updated().
  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  // ── Private: ARIA synchronisation ──────────────────────────────────────────

  // Reflects active state to hidden attribute and tabindex on the host element.
  // aria-labelledby is reflected directly by the labelledBy setter.
  #syncHostAriaAttributes() {
    if (this.#active) {
      this.removeAttribute('hidden');
      this.setAttribute('tabindex', '0');
    } else {
      this.setAttribute('hidden', '');
      this.setAttribute('tabindex', '-1');
    }
  }
}

if (!customElements.get('run-tab-panel')) {
  customElements.define('run-tab-panel', RunTabPanel);
}

export { RunTabPanel };
export default RunTabPanel;
